import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { MessageCircle, MapPin, CheckCircle, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { doctors } from "./doctors._data";

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// const API = `${BACKEND_URL}/api`;

export default function DoctorProfile() {
  const { id } = useParams();
  // const [doctor, setDoctor] = useState(null);
  // const [loading, setLoading] = useState(true);

  const doctor = doctors.find((doc) => doc.id == id);
  const loading = false;
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!doctor) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h2 className="text-2xl font-heading font-semibold text-[#2D3436] mb-4">
          Doctor not found
        </h2>
        <Link to="/doctors">
          <Button className="bg-primary hover:bg-[#3A5233] text-white rounded-full">
            Back to Doctors
          </Button>
        </Link>
      </div>
    );
  }

  const whatsappUrl = `https://wa.me/${doctor.whatsapp_number}?text=${encodeURIComponent(
    `Hello! I would like to consult Dr. ${doctor.name}.`,
  )}`;

  return (
    <div className="min-h-screen py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/doctors"
          className="inline-flex items-center text-primary hover:text-[#3A5233] mb-8"
          data-testid="back-to-doctors"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Doctors
        </Link>

        <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            <div className="relative h-96 md:h-auto bg-accent">
              {doctor.photo_url ? (
                <img
                  src={doctor.photo_url}
                  alt={doctor.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-secondary/10">
                  <div className="w-40 h-40 bg-primary/20 rounded-full flex items-center justify-center">
                    <span className="text-7xl font-heading text-primary">
                      {doctor.name.charAt(0)}
                    </span>
                  </div>
                </div>
              )}
              {doctor.available && (
                <div className="absolute top-6 left-6 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-medium flex items-center space-x-2">
                  <CheckCircle size={18} />
                  <span>Available</span>
                </div>
              )}
            </div>

            <div className="p-8 md:p-12">
              <h1
                className="text-4xl font-heading font-bold text-[#2D3436] mb-3"
                data-testid="doctor-name"
              >
                {doctor.name}
              </h1>
              <p className="text-lg text-[#636E72] mb-4">
                {doctor.qualifications}
              </p>

              {doctor.location && (
                <div className="flex items-center space-x-2 text-[#636E72] mb-6">
                  <MapPin size={18} className="text-primary" />
                  <span>{doctor.location}</span>
                </div>
              )}

              {doctor.bio && (
                <p className="text-[#636E72] leading-relaxed mb-6">
                  {doctor.bio}
                </p>
              )}

              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button
                  className="w-full bg-[#25D366] hover:bg-[#20B558] text-white rounded-full py-6 text-lg"
                  data-testid="consult-button"
                >
                  <MessageCircle size={20} className="mr-2" />
                  Consult on WhatsApp
                </Button>
              </a>
            </div>
          </div>

          <div className="p-8 md:p-12 border-t border-border/50">
            <h2 className="text-2xl font-heading font-semibold text-[#2D3436] mb-6">
              Specializations
            </h2>
            <div className="flex flex-wrap gap-3 mb-8">
              {doctor.specializations.map((spec, idx) => (
                <span
                  key={idx}
                  className="px-4 py-2 bg-accent text-[#2D3436] text-sm font-medium rounded-full"
                >
                  {spec}
                </span>
              ))}
            </div>

            <h2 className="text-2xl font-heading font-semibold text-[#2D3436] mb-6">
              Areas of Expertise
            </h2>
            <ul className="space-y-3">
              {doctor.expertise.map((exp, idx) => (
                <li key={idx} className="flex items-start space-x-3">
                  <CheckCircle
                    size={20}
                    className="text-primary mt-1 flex-shrink-0"
                  />
                  <span className="text-[#636E72] leading-relaxed">{exp}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
